import { Grid } from '@mantine/core';
import AwesomeDebouncePromise from 'awesome-debounce-promise';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router';
import useFetch from '../../helpers/useFetch';
import ErrorMessage from '../ErrorMessage';
import Spinner from '../Spinner';
import ConversionRateCard from './ConversionRateCard';

const conversionRateDataDebounced = AwesomeDebouncePromise((fetch, fetchUrl) => {
  return fetch(fetchUrl);
}, 300);

const prevConversionRateDataDebounced = AwesomeDebouncePromise((fetch, fetchUrl) => {
  return fetch(fetchUrl);
}, 300);

const ENGAGEMENT_CAVEAT =
  'Correlational engagement segmentation, not a controlled test — it is not a measure of ' +
  "Releva's incremental effect. For causal lift, compare a control-group holdout (enable a " +
  'control group in domain settings).';

const SummaryConversionRate = ({ periods }) => {
  const [conversionRateData, setConversionRateData] = useState();
  const [prevConversionRateData, setPrevConversionRateData] = useState();
  const [error, setError] = useState();

  const { accountId, domainId } = useParams();
  const fetch = useFetch();

  const calculateConversionRate = (profiles, count) => {
    const profilesNum = Number(profiles) || 0;
    const countNum = Number(count) || 0;

    if (profilesNum === 0) {
      return 0;
    }

    return countNum / profilesNum;
  };

  // periods.current.start/end and periods.prev.start/end are now ISO date strings
  const fetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/conversionRateStats/${periods.current.start}/${periods.current.end}`;
  const prevFetchUrl = `/api/admin/accounts/${accountId}/domains/${domainId}/conversionRateStats/${periods.prev.start}/${periods.prev.end}`;

  // No tab guard needed here: DashboardPage's Tabs uses keepMounted={false}, so this
  // component (rendered only from ConversionTab) is itself mounted and unmounted with
  // the tab. The guard used to check mainTab === 'engagement' from when this lived at
  // the tail of the Engagement tab; left in place after the move to Conversion, the
  // condition was never true and the fetch never fired.
  useEffect(() => {
    setConversionRateData(undefined);
    setPrevConversionRateData(undefined);
    conversionRateDataDebounced(fetch, fetchUrl).then(setConversionRateData).catch(setError);
    prevConversionRateDataDebounced(fetch, prevFetchUrl).then(setPrevConversionRateData).catch(setError);
  }, [fetch, fetchUrl, prevFetchUrl]);

  if (error) {
    return <ErrorMessage message={error.message} />;
  }

  if (conversionRateData === undefined || prevConversionRateData === undefined) {
    return (
      <Grid.Col span={12} mb="lg">
        <Spinner />
      </Grid.Col>
    );
  }

  // `profiles` holds every denominator below. A 200 carrying an unexpected body
  // would otherwise throw straight out of render — and with no error boundary
  // above it, that white-screens the whole tab rather than one card. The
  // numerators were already optional-chained; this makes the denominators as
  // careful, since a rate cannot be computed without them at all.
  if (!conversionRateData.profiles || !prevConversionRateData.profiles) {
    return <ErrorMessage message="Conversion rates are unavailable for this period." />;
  }

  return (
    <>
      <ConversionRateCard
        value={calculateConversionRate(conversionRateData.profiles.all, conversionRateData.converters?.all)}
        previousValue={calculateConversionRate(
          prevConversionRateData.profiles.all,
          prevConversionRateData.converters?.all
        )}
        tittle="Conversion rate"
      />
      <ConversionRateCard
        value={calculateConversionRate(conversionRateData.profiles.releva, conversionRateData.converters?.releva)}
        previousValue={calculateConversionRate(
          prevConversionRateData.profiles.releva,
          prevConversionRateData.converters?.releva
        )}
        tittle="Conversion rate · Releva-engaged"
        tooltip={ENGAGEMENT_CAVEAT}
      />
      <ConversionRateCard
        value={calculateConversionRate(conversionRateData.profiles.nonReleva, conversionRateData.converters?.nonReleva)}
        previousValue={calculateConversionRate(
          prevConversionRateData.profiles.nonReleva,
          prevConversionRateData.converters?.nonReleva
        )}
        tittle="Conversion rate · Not engaged"
        tooltip={ENGAGEMENT_CAVEAT}
      />
    </>
  );
};

export default SummaryConversionRate;
